import * as React from 'react';
import { cn } from '../../lib/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
  /** Forwarded to the segment so tests can target a specific choice. */
  testId?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value?: T;
  onChange?: (_value: T) => void;
  /** Names the group for screen readers; use when there is no visible label. */
  'aria-label'?: string;
  /** Points at a visible label element instead. */
  'aria-labelledby'?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const sizes = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
} as const;

/**
 * A small set of mutually exclusive choices, shown side by side.
 *
 * Built on the radiogroup role rather than tabs: these choose a value in a
 * form, they don't switch panels, and assistive tech should announce them as
 * a single choice. Arrow keys move between segments and select as they go,
 * which is the expected behaviour for a radio group.
 *
 * Suits two or three short, symmetrical options. When the choices carry
 * different configuration underneath, use RadioRows instead — this control
 * has nowhere to put it.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
  size = 'md',
  className,
  ...aria
}: SegmentedControlProps<T>) {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const selectableIndexes = options
    .map((option, index) => (option.disabled || disabled ? -1 : index))
    .filter((index) => index !== -1);

  const move = (from: number, step: 1 | -1) => {
    if (selectableIndexes.length === 0) return;
    const position = selectableIndexes.indexOf(from);
    const next =
      selectableIndexes[
        (position + step + selectableIndexes.length) % selectableIndexes.length
      ]!;
    refs.current[next]?.focus();
    onChange?.(options[next]!.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(index, 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(index, -1);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={aria['aria-label']}
      aria-labelledby={aria['aria-labelledby']}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5',
        disabled && 'opacity-60',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const isDisabled = disabled || option.disabled;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={isDisabled}
            data-testid={option.testId}
            // Only the selected segment is tabbable, so the group is one tab
            // stop and arrow keys move within it.
            tabIndex={selected || (value === undefined && index === 0) ? 0 : -1}
            onClick={() => !isDisabled && onChange?.(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'rounded-md px-3 font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              sizes[size],
              // Selected is stated in the accent colour, not by a white pill
              // on a near-white track. Review: "make those segmented switch
              // things a little bit more obvious". A card-on-muted pill in the
              // light theme is two greys a shade apart, and the ring and the
              // weight were doing all the work. This is the same way the app
              // already says "selected" in the triage list — a primary tint
              // and a primary edge.
              selected
                ? 'bg-card font-semibold text-primary shadow-sm ring-1 ring-primary/50'
                : 'text-muted-foreground hover:text-foreground',
              isDisabled && 'cursor-not-allowed hover:text-muted-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
